import { Component, OnInit, Input, OnDestroy, ViewChild } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { interval } from "rxjs";
import { filter } from "rxjs/operators";
import { SubSink } from "subsink";

import {
  AuthenticationService,
  ConfigurationService,
} from "@/shared-module/services";
import { Menu, Configuration, LanguageChoice, MenuItem } from "@/shared-module/models";
import {
  NotAccessibleDialogComponent,
  LanguageSelectionDialogComponent,
} from "@/shared-module/components";

@Component({ selector: "app-topmenu", templateUrl: "topmenu.component.html" })
export class TopMenuComponent implements OnInit, OnDestroy {
  @ViewChild("dialog", { static: true }) dialog: NotAccessibleDialogComponent;
  @ViewChild("languageSelectionDialog", { static: true })
  languageSelectionDialog: LanguageSelectionDialogComponent;
  @Input() courseName: string;
  @Input() menu: Menu;
  @Input() nextDecision: Date;
  timeLeft: string;
  timeExceeded: boolean;
  subscription: any;
  simulation: string;
  cssColor: string;
  rgbColor: string;
  logoImage: string;
  logoutText: string;
  documentsText: string;
  documentUrl: string;
  firstPageUrl: string;
  onboardingUrl: string;
  onboardingText: string;
  teamText: string;
  teamUrl: string;
  languagesText: string;
  supportedLanguages: LanguageChoice[];
  currentLanguage: string;
  subs = new SubSink();
  displayTimer = true;
  displayTeam = false;
  noRankingMode = false;
  isDemo = false;
  blockedByDemoTexts: any;
  displayLogOut: boolean;
  skipLinkPath: string;
  screenReaderFriendly: boolean;
  highContrast: boolean;
  enableHighContrastsText: string;
  disableHighContrastsText: string;
  enableScreenReaderText: string;
  disableScreenReaderText: string;
  enableScreenReaderMode: boolean;
  enableHighContrasts: boolean;
  path: string;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private configurationService: ConfigurationService,
  ) {
    const thisObject = this;
    this.subs.sink = this.configurationService.config.subscribe(
      (c: Configuration) => {
        thisObject.cssColor = c.cssColor;
        if (c.highContrast) {
          thisObject.logoImage = `img/logo-${c.cssColor}-${c.simulation}-hc.svg`;
        } else {
          thisObject.logoImage = `img/logo-${c.cssColor}-${c.simulation}.svg`;
        }
        this.noRankingMode = c.noRankingMode;
        this.logoutText = c.logoutText;
        this.documentsText = c.documentsText;
        this.documentUrl = c.documentUrl;
        this.firstPageUrl = c.firstPageUrl;
        this.onboardingUrl = c.onboardingUrl;
        this.teamUrl = c.teamUrl;
        this.onboardingText = c.onboardingText;
        this.teamText = c.teamText;
        this.displayTeam = c.displayTeam;
        this.languagesText = c.languagesText;
        this.rgbColor = c.rgb;
        this.supportedLanguages = c.supportedLanguages;
        this.currentLanguage = c.currentLanguage;
        this.isDemo = c.isDemo;
        this.blockedByDemoTexts = c.blockedByDemoTexts;
        this.displayLogOut = c.displayLogOut;
        this.screenReaderFriendly = c.screenReaderFriendly;
        this.highContrast = c.highContrast;
        this.enableHighContrastsText = c.enableHighContrastsText;
        this.disableHighContrastsText = c.disableHighContrastsText;
        this.enableScreenReaderText = c.enableScreenReaderText;
        this.disableScreenReaderText = c.disableScreenReaderText;
        this.enableScreenReaderMode = c.enableScreenReaderMode;
        this.enableHighContrasts = c.enableHighContrasts;
        this.simulation = c.simulation;
      },
    );

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.router.url.endsWith("#main-content")) {
          this.skipLinkPath = `${this.router.url}#main-content`;
        }
      });
  }

  ngOnInit() {
    this.updateTimer();
    const counter = interval(1000);
    const thisObject = this;
    this.subs.sink = counter.subscribe(() => {
      thisObject.updateTimer();
    });
  }

  getSelectedPath() {}

  updateTimer() {
    try {
      if (this.nextDecision) {
        const nextDate: any = new Date(this.nextDecision + "Z");
        const now: any = new Date();
        let seconds = (nextDate - now) / 1000;
        if (seconds < 0) {
          this.timeExceeded = true;
          seconds = -seconds;
        } else {
          this.timeExceeded = false;
        }
        this.timeLeft = this.secondsToDhms(seconds);
      } else {
        this.timeLeft = "";
        this.timeExceeded = false;
      }
    } catch (e) {
      console.log(e);
    }
  }

  hasChildren(itm) {
    let v = false;

    if (itm) {
      if (itm.items) {
        if (itm.items.length > 0) {
          if (itm.items[0].items) {
            if (itm.items[0].items.length === 0) {
              v = true;
            }
          }
        }
      }
    }

    return v;
  }

  hasChildren2(itm) {
    let v = false;

    if (itm) {
      if (itm.items) {
        if (itm.items.length > 0) {
          if (itm.items[0].items) {
            if (itm.items[0].items.length > 0) {
              v = true;
            }
          }
        }
      }
    }
    // temporary solution !
    if (itm.isDecision) {
      return true;
    }
    return v;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  logout() {
    this.authenticationService.logout();

    if (this.menu.signOutUrl) {
      window.open(this.menu.signOutUrl, "_self");
    } else {
      this.router.navigate(["/login"]);
    }
  }

  openNotAccessible() {
    this.dialog.open();
  }

  openLanguageSelector() {
    this.languageSelectionDialog.open();
  }

  secondsToDhms(seconds) {
    seconds = Number(seconds);
    const d = Math.floor(seconds / (3600 * 24));
    let h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    let txt = "";
    h = d * 24 + h;
    if (h < 10) {
      txt += "0" + h + ":";
    } else {
      txt += h + ":";
    }
    if (m < 10) {
      txt += "0" + m + ":";
    } else {
      txt += m + ":";
    }
    if (s < 10) {
      txt += "0" + s;
    } else {
      txt += s;
    }

    if (Math.abs(h) > 750) {
      this.displayTimer = false;
      return "";
    }
    this.displayTimer = true;
    return txt;
  }

  changeLanguage(language) {
    this.configurationService
      .setLanguage(language)
      .subscribe((s) => location.reload());
  }

  setHighContrast() {
    this.configurationService.setHighContrast().subscribe((s) => {
      location.reload();
    });
  }
  unsetHighContrast() {
    this.configurationService.unsetHighContrast().subscribe((s) => {
      location.reload();
    });
  }
  setScreenReaderFriendly() {
    this.configurationService.setScreenReaderFriendly().subscribe((s) => {
      location.reload();
    });
  }
  unScreenReaderFriendly() {
    this.configurationService.unsetScreenReaderFriendly().subscribe((s) => {
      location.reload();
    });
  }

  linkAriaLabel(item: MenuItem, parent?: MenuItem): string {
    if (!parent) return item.caption;
    return `${parent.caption} – ${item.caption}`;
  }

  isSelected(path: string): boolean {
    if (this.router.url != null && path != null) {
      var url = this.router.url;
      if (url.indexOf("?") > 0)
        url = url.substring(0, this.router.url.indexOf("?"));
      var p1 = url.split("/");
      var p2 = path.split("/");
      if (p1.length >= 3 && p2.length >= 3)
        return p1[1] === p2[1] && p1[2] === p2[2];
      else if (p1.length >= 2 && p2.length >= 2) return p1[1] === p2[1];
    }
    return false;
  }
}
